import type { LucideIcon } from 'lucide-react';
import type { ButtonHTMLAttributes } from 'react';

export interface IconButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {
  icon: LucideIcon;
  label: string;
  size?: number;
  active?: boolean;
  tone?: 'default' | 'glass' | 'accent';
}

/** docs/spec/06 §2 IconButton: square, radius 8, `title` tooltip = label, ≥ 44 px hit area. */
export function IconButton({
  icon: Icon,
  label,
  size = 44,
  active,
  tone = 'default',
  className = '',
  style,
  ...rest
}: IconButtonProps) {
  const bg = active
    ? 'bg-selected text-selected-ink'
    : tone === 'accent'
      ? 'bg-accent text-accent-ink hover:bg-accent-pressed'
      : tone === 'glass'
        ? 'glass text-text hover:bg-white/8'
        : 'text-text hover:bg-white/8 active:bg-white/8';
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      aria-pressed={active === undefined ? undefined : active}
      {...rest}
      className={`relative inline-flex shrink-0 items-center justify-center rounded-md transition-colors disabled:opacity-35 ${bg} ${className}`}
      style={{ width: size, height: size, ...style }}
    >
      <Icon size={Math.round(size * 0.45)} strokeWidth={2.2} aria-hidden />
    </button>
  );
}
